import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { toast } from '@surface/components';

import {
  createTransitionFromTemplateApiTransitionsFromTemplatePost,
  getTransitionApiTransitionsTransitionIdGet,
  saveTransitionAsTemplateApiTransitionsTransitionIdSaveAsTemplatePost,
  type TransitionInput as Transition,
  updateTransitionApiTransitionsTransitionIdPatch,
} from '@/api/client';

import {
  companyRolesFromFormValues,
  formatTargetCompletionDate,
} from '../_lib/transition-settings-form-model';
import { transitionTemplatesQueryKey } from './use-transition-templates';

import type {
  PropertyTransitionsCreateSubmitValues,
  PropertyTransitionSettingsEditSubmitValues,
} from '../property-transitions-types';

export const transitionQueryKey = (transitionId: string) =>
  ['transition', transitionId] as const;

export const useTransition = (
  transitionId: string | undefined,
  options?: Omit<
    UseQueryOptions<Transition>,
    'queryKey' | 'queryFn' | 'enabled'
  >
) => {
  return useQuery({
    queryKey: transitionQueryKey(transitionId ?? ''),
    queryFn: () =>
      getTransitionApiTransitionsTransitionIdGet({
        transitionId: transitionId!,
      }),
    enabled: Boolean(transitionId),
    staleTime: 60 * 1000,
    ...options,
  });
};

/**
 * Creates a property transition from templates. Invalidates transition templates on success.
 * Pass `onSuccess` / `onError` for navigation and UI feedback at the callsite.
 */
export const useCreatePropertyTransition = (
  options?: Omit<
    UseMutationOptions<
      Transition,
      Error,
      PropertyTransitionsCreateSubmitValues
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: PropertyTransitionsCreateSubmitValues) =>
      createTransitionFromTemplateApiTransitionsFromTemplatePost({
        name: data.name.trim(),
        template_ids: data.templateIds,
        company_roles: companyRolesFromFormValues(data),
        target_completion_date: formatTargetCompletionDate(
          data.targetCompletionDate
        ),
      }),
    onSuccess: (data, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: transitionTemplatesQueryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: ['transitions', 'list'],
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export type UpdatePropertyTransitionVariables =
  PropertyTransitionSettingsEditSubmitValues & {
    transitionId: string;
  };

/**
 * Updates transition settings (name and completion date).
 * Invalidates the transition detail and list queries on success.
 */
export const useUpdatePropertyTransition = (
  options?: Omit<
    UseMutationOptions<Transition, Error, UpdatePropertyTransitionVariables>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({
      transitionId,
      ...data
    }: UpdatePropertyTransitionVariables) =>
      updateTransitionApiTransitionsTransitionIdPatch(
        { transitionId },
        {
          name: data.name.trim(),
          target_completion_date: formatTargetCompletionDate(
            data.targetCompletionDate
          ),
        }
      ),
    onSuccess: (data, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: transitionQueryKey(variables.transitionId),
      });
      void queryClient.invalidateQueries({
        queryKey: ['transitions', 'list'],
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export type PatchTransitionTargetCompletionVariables = {
  transitionId: string;
  targetCompletionDate: Date | undefined;
};

/**
 * PATCHes only the transition target completion date from the header chip.
 */
export const usePatchTransitionTargetCompletion = (
  options?: Omit<
    UseMutationOptions<
      Transition,
      Error,
      PatchTransitionTargetCompletionVariables
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({
      transitionId,
      targetCompletionDate,
    }: PatchTransitionTargetCompletionVariables) =>
      updateTransitionApiTransitionsTransitionIdPatch(
        { transitionId },
        {
          target_completion_date: targetCompletionDate
            ? formatTargetCompletionDate(targetCompletionDate)
            : null,
        }
      ),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData(
        transitionQueryKey(variables.transitionId),
        data
      );
      void queryClient.invalidateQueries({
        queryKey: ['transitions', 'list'],
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast.error('Failed to update due date', {
        description: error.message || 'Please try again.',
      });
      options?.onError?.(error, variables, onMutateResult, context);
    },
  });
};

export type SaveTransitionAsTemplateVariables = {
  transitionId: string;
  /** Optional name for the cloned template. Defaults to the source transition's name. */
  name?: string;
};

/**
 * Deep-clones a live transition into a new *template* transition
 * (``is_template=true``) via ``POST /api/transitions/{id}/save-as-template``.
 * The source transition is unchanged. Invalidates the templates list so the
 * new template appears in the manage-templates view; consumers typically
 * navigate to the returned template's detail page from ``onSuccess``.
 */
export const useSaveTransitionAsTemplate = (
  options?: Omit<
    UseMutationOptions<Transition, Error, SaveTransitionAsTemplateVariables>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ transitionId, name }: SaveTransitionAsTemplateVariables) =>
      saveTransitionAsTemplateApiTransitionsTransitionIdSaveAsTemplatePost(
        { transitionId },
        { name: name?.trim() ? name.trim() : null }
      ),
    onSuccess: (data, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: transitionTemplatesQueryKey,
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
